'use client';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui';
import Link from 'next/link';
import type { JSX, ReactNode } from 'react';

export interface IPipelineStepDialogAction {
  label: string;
  onClick: () => void;
  pending: boolean;
  disabledReason?: string;
}

interface IPipelineStepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  dataHref?: string;
  dataLabel?: string;
  primaryAction?: IPipelineStepDialogAction;
  secondaryAction?: IPipelineStepDialogAction;
  body?: ReactNode;
}

export function PipelineStepDialog({
  open,
  onOpenChange,
  title,
  description,
  dataHref,
  dataLabel = 'Open data',
  primaryAction,
  secondaryAction,
  body,
}: IPipelineStepDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter className="gap-2 sm:gap-2">
          {dataHref && (
            <Button variant="outline" asChild>
              <Link href={dataHref}>{dataLabel}</Link>
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outline"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.pending || Boolean(secondaryAction.disabledReason)}
              title={secondaryAction.disabledReason}
            >
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              disabled={primaryAction.pending || Boolean(primaryAction.disabledReason)}
              title={primaryAction.disabledReason}
            >
              {primaryAction.label}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
